import * as React from "react";
import { Link, Prompt, useParams } from "react-router-dom";

import { ConnectTeleopdButton } from "Components/Tenants";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorState,
  Page,
  PageHeader,
  Skeleton,
  StatusBadge,
  Switch,
} from "Components/ui";
import { Import, LayoutGrid, RefreshCw } from "Components/ui/icons";
import { getApiErrorMessage } from "api/apiError";
import { cn } from "utils/cn";
import { formatDateTime, formatRelative } from "utils/format";
import useClientModulesData from "./data";

/** /clients/:tenantId/modules — choose the TeleOpd navbar modules SAP applies for a client. */
const ClientModulesPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const {
    states,
    entitlementsQuery,
    setManaged,
    toggleModule,
    setGroup,
    importFromTeleopd,
    discard,
    save,
  } = useClientModulesData(tenantId);
  const {
    canEdit,
    locked,
    unconnected,
    canConnect,
    entitlements,
    clientName,
    clientSlug,
    clientUri,
    groups,
    catalog,
    draft,
    selected,
    dirty,
    localModules,
    importable,
    unknownLocal,
    saving,
  } = states;
  const disabled = !canEdit || saving;
  const importMatches =
    importable.length === selected.length && importable.every((name) => selected.includes(name));

  const header = (
    <PageHeader
      breadcrumbs={[{ label: "Tenants", to: "/clients" }, { label: clientName || "Client" }]}
      title="TeleOpd modules"
      meta={
        <>
          {clientSlug && (
            <Badge size="md" className="font-mono" title="pm2 app">
              {clientSlug}
            </Badge>
          )}
          {entitlements && <StatusBadge domain="stage" status={entitlements.stage} size="md" />}
        </>
      }
      description={
        <>
          {clientUri && <span className="block truncate font-mono text-xs">{clientUri}</span>}
          Choose which TeleOpd navbar modules this client can use. TeleOpd still shows a module only
          to roles that have its permission.
        </>
      }
    />
  );

  let body: React.ReactNode;
  if (entitlementsQuery.isLoading) {
    body = (
      <div className="space-y-6" aria-busy="true">
        <Skeleton className="h-24" />
        <Skeleton className="h-32" />
        <Skeleton className="h-72" />
      </div>
    );
  } else if (entitlementsQuery.isError || !entitlements) {
    body = (
      <Card>
        <ErrorState
          title="Modules didn't load"
          description={getApiErrorMessage(entitlementsQuery.error)}
          action={
            <Button
              variant="secondary"
              leftIcon={<RefreshCw />}
              onClick={() => entitlementsQuery.refetch()}
            >
              Try again
            </Button>
          }
        />
      </Card>
    );
  } else if (unconnected) {
    // Not connected: no module settings to act on; only the next step towards connecting.
    body = (
      <Alert
        tone={locked ? "neutral" : "info"}
        title="TeleOpd isn't connected yet"
        action={
          locked ? (
            <Button asChild size="sm" variant="secondary">
              <Link to="/clients">Open Tenants</Link>
            </Button>
          ) : canConnect ? (
            <ConnectTeleopdButton tenantId={tenantId} />
          ) : undefined
        }
      >
        {locked
          ? "Modules reach TeleOpd in its sync, so they can be chosen once it is connected. This client is Locked: make it actionable on the Tenants page first."
          : "Modules reach TeleOpd in its sync, so they can be chosen once it is connected and has synced with SAP."}
      </Alert>
    );
  } else {
    body = (
      <div className="space-y-6">
        {locked ? (
          <Alert tone="neutral" title="Locked until production release">
            SAP is in testing, so this client's modules can't change here. Move it to Actionable on
            the Tenants page to work on it.
          </Alert>
        ) : (
          !canEdit && (
            <Alert tone="neutral">
              You can view these settings. Changing them needs tenant write access.
            </Alert>
          )
        )}

        <Card>
          <CardBody className="space-y-4">
            <Switch
              checked={draft.managed}
              onCheckedChange={setManaged}
              disabled={disabled}
              label="Managed by SAP"
              description={
                draft.managed
                  ? "On: SAP's selection below replaces TeleOpd's own module list on its next sync (SAP nudges TeleOpd right after you save)."
                  : "Off: TeleOpd keeps its own module list. The selection below is saved but not applied."
              }
            />
            {draft.managed && selected.length === 0 && (
              <Alert tone="warning">
                No modules are selected, so TeleOpd will hide every module in the catalog.
              </Alert>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="TeleOpd currently shows"
            description={
              entitlements.localModulesAt
                ? `Reported ${formatRelative(entitlements.localModulesAt)} (${formatDateTime(
                    entitlements.localModulesAt
                  )}), before any SAP selection.`
                : "TeleOpd's own module list, reported on each sync."
            }
            actions={
              canEdit && localModules.length > 0 ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={importFromTeleopd}
                  disabled={disabled || importMatches}
                  leftIcon={<Import />}
                  title="Copy TeleOpd's current modules into the selection below"
                >
                  Import from TeleOpd
                </Button>
              ) : null
            }
          />
          <CardBody>
            {localModules.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                TeleOpd hasn't reported its modules yet.
              </p>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {localModules.map((name) => (
                    <Badge key={name} size="md">
                      {catalog.find((entry) => entry.name === name)?.label || name}
                    </Badge>
                  ))}
                </div>
                {unknownLocal.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Not in SAP's catalog (left untouched by TeleOpd): {unknownLocal.join(", ")}
                  </p>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Modules"
            description={`${selected.length} of ${catalog.length} selected`}
          />
          {groups.length === 0 ? (
            <EmptyState compact icon={<LayoutGrid />} title="No modules in the catalog" />
          ) : (
            <div className="mt-4 divide-y divide-border border-t border-border">
              {groups.map((group) => {
                const selectedCount = group.modules.filter((entry) =>
                  selected.includes(entry.name)
                ).length;
                const all = selectedCount === group.modules.length;
                return (
                  <section key={group.name} aria-label={group.name} className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={all ? true : selectedCount > 0 ? "indeterminate" : false}
                        onCheckedChange={(value) => setGroup(group, value === true)}
                        disabled={disabled}
                        label={group.name}
                      />
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {selectedCount}/{group.modules.length}
                      </span>
                    </div>
                    <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {group.modules.map((entry) => {
                        const checked = selected.includes(entry.name);
                        return (
                          <li
                            key={entry.name}
                            className={cn(
                              "rounded-control border px-3 py-2 transition-colors duration-fast",
                              checked
                                ? "border-primary/40 bg-primary-subtle"
                                : "border-border bg-surface",
                              !disabled && !checked && "hover:border-primary/30"
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleModule(entry.name)}
                              disabled={disabled}
                              label={entry.label}
                              description={
                                <span className="font-mono">
                                  {entry.name} · {entry.permissionKey}
                                </span>
                              }
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </Card>

        {entitlements.updatedAt && (
          <p className="text-xs text-subtle-foreground">
            Last saved {formatDateTime(entitlements.updatedAt)}.
          </p>
        )}
      </div>
    );
  }

  return (
    <Page width="narrow" className="max-w-4xl">
      <Prompt when={dirty} message="You have unsaved module changes. Leave without saving?" />
      {header}
      {body}

      {canEdit && (dirty || saving) && (
        // Sticky inside the content column: a fixed full-width bar slides under the app sidebar.
        <div className="sticky bottom-4 z-30 rounded-card border border-border bg-surface-raised px-4 py-3 shadow-popover">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-sm text-foreground">
              <span aria-hidden="true" className="h-2 w-2 rounded-full bg-warning" />
              Unsaved changes
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={discard} disabled={saving}>
                Discard
              </Button>
              <Button onClick={save} loading={saving}>
                Save modules
              </Button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};

export default ClientModulesPage;
